import { useEffect, useState } from 'react';

const REQUIRED_FIELDS = ['id', 'image_url', 'price'] as const;
const OPTIONAL_FIELDS = ['title', 'brand', 'sale_price', 'rating', 'currency'] as const;
type ProductField = typeof REQUIRED_FIELDS[number] | typeof OPTIONAL_FIELDS[number];

const FIELD_LABEL: Record<ProductField, string> = {
  id: 'Product ID',
  image_url: 'Image URL',
  price: 'Price',
  title: 'Title',
  brand: 'Brand',
  sale_price: 'Sale price',
  rating: 'Rating',
  currency: 'Currency',
};

// Heuristic auto-suggest from a list of column names. Picks the first column
// whose name contains the Product field name (or a known synonym), so the user
// usually only has to confirm rather than choose. Returns null if nothing fits.
const FIELD_HINTS: Record<ProductField, string[]> = {
  id: ['product_id', 'id', 'sku'],
  image_url: ['image_url', 'image', 'img', 'picture'],
  price: ['current_price', 'price', 'list_price', 'unit_price'],
  title: ['title', 'name', 'product_name'],
  brand: ['brand', 'manufacturer', 'vendor'],
  sale_price: ['sale_price', 'discount_price', 'special_price'],
  rating: ['rating', 'review_score', 'stars'],
  currency: ['currency', 'currency_code', 'iso_currency'],
};

function autoSuggest(field: ProductField, columns: string[]): string | null {
  const lc = columns.map((c) => c.toLowerCase());
  for (const hint of FIELD_HINTS[field]) {
    const hit = lc.indexOf(hint.toLowerCase());
    if (hit !== -1) return columns[hit];
  }
  // Fall back to substring containment.
  for (const hint of FIELD_HINTS[field]) {
    const hit = lc.findIndex((c) => c.includes(hint.toLowerCase()));
    if (hit !== -1) return columns[hit];
  }
  return null;
}

export interface ColumnMapFormProps {
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  /**
   * If provided, render the form as dropdowns of these column names. If null
   * (BQ table not yet entered, or schema fetch failed), fall back to free-text
   * inputs so the user can still configure manually.
   */
  availableColumns?: string[] | null;
  /** Hint copy shown above the form. */
  hint?: string;
}

export default function ColumnMapForm({
  value,
  onChange,
  availableColumns,
  hint,
}: ColumnMapFormProps) {
  // Local mirror so users can clear an optional field by emptying the input.
  const [local, setLocal] = useState<Record<ProductField, string>>(() =>
    fromMap(value),
  );

  useEffect(() => {
    setLocal(fromMap(value));
  }, [value]);

  // When a fresh schema arrives, seed any unfilled fields with auto-suggestions
  // — but only if the user hasn't already typed something. Required fields stay
  // empty until either auto-matched or set explicitly so the Save validator can
  // catch missing rows.
  const [didAutoFill, setDidAutoFill] = useState<string | null>(null);
  useEffect(() => {
    if (!availableColumns || availableColumns.length === 0) return;
    const key = availableColumns.join('|');
    if (didAutoFill === key) return;
    const next: Record<ProductField, string> = { ...local };
    let changed = false;
    for (const f of [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS] as ProductField[]) {
      if (next[f]) continue;
      const guess = autoSuggest(f, availableColumns);
      if (guess) {
        next[f] = guess;
        changed = true;
      }
    }
    setDidAutoFill(key);
    if (changed) {
      setLocal(next);
      emit(next);
    }
    // We intentionally only re-run when availableColumns changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableColumns]);

  function emit(next: Record<ProductField, string>) {
    const cleaned: Record<string, string> = {};
    (Object.entries(next) as [ProductField, string][]).forEach(([k, v]) => {
      const trimmed = v.trim();
      if (REQUIRED_FIELDS.includes(k as typeof REQUIRED_FIELDS[number])) {
        cleaned[k] = trimmed;
      } else if (trimmed) {
        cleaned[k] = trimmed;
      }
    });
    onChange(cleaned);
  }

  function setField(field: ProductField, raw: string) {
    const next = { ...local, [field]: raw };
    setLocal(next);
    emit(next);
  }

  const showDropdowns = !!(availableColumns && availableColumns.length > 0);

  return (
    <div className="space-y-2.5" data-testid="column-map-form">
      <div className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5 items-center">
        {REQUIRED_FIELDS.map((f) => (
          <FieldRow
            key={f}
            field={f}
            required
            value={local[f] ?? ''}
            options={showDropdowns ? availableColumns! : null}
            onChange={(v) => setField(f, v)}
          />
        ))}
        <div className="col-span-2 my-1 border-t border-ff-sand/50" />
        {OPTIONAL_FIELDS.map((f) => (
          <FieldRow
            key={f}
            field={f}
            required={false}
            value={local[f] ?? ''}
            options={showDropdowns ? availableColumns! : null}
            onChange={(v) => setField(f, v)}
          />
        ))}
      </div>
      <p className="text-[11px] text-ff-mute leading-relaxed">
        {hint ??
          (showDropdowns
            ? 'Pick the column to read each Product field from. Required rows have a red asterisk; optional rows can be left as “— skip —”.'
            : 'Map each Product field to the column name in your BigQuery table. Required fields are marked with *; leave optional rows blank to skip them.')}
      </p>
    </div>
  );
}

function fromMap(value: Record<string, string>): Record<ProductField, string> {
  const out: Record<ProductField, string> = {
    id: '',
    image_url: '',
    price: '',
    title: '',
    brand: '',
    sale_price: '',
    rating: '',
    currency: '',
  };
  for (const k of Object.keys(out) as ProductField[]) {
    if (typeof value[k] === 'string') out[k] = value[k];
  }
  return out;
}

function FieldRow({
  field,
  required,
  value,
  options,
  onChange,
}: {
  field: ProductField;
  required: boolean;
  value: string;
  options: string[] | null;
  onChange: (v: string) => void;
}) {
  const id = `column-map-${field}`;
  const placeholder = required ? `column for ${field}` : 'leave blank to skip';

  return (
    <>
      <label
        htmlFor={id}
        className="font-mono text-[11px] uppercase tracking-widish text-ff-mute"
      >
        {FIELD_LABEL[field]}
        {required && <span className="text-ff-danger ml-0.5">*</span>}
      </label>
      {options ? (
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`ff-input text-sm py-2 ${
            required && !value ? 'border-ff-danger/60' : ''
          }`}
          data-testid={`column-map-input-${field}`}
        >
          <option value="">
            {required ? '— select column —' : '— skip —'}
          </option>
          {options.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
          {/* If the previously-saved value isn't in the schema, surface it as
              a non-default fallback so we don't silently lose the user's
              setting. */}
          {value && !options.includes(value) && (
            <option value={value}>{value} (not in schema)</option>
          )}
        </select>
      ) : (
        <input
          id={id}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="ff-input text-sm py-2"
          data-testid={`column-map-input-${field}`}
        />
      )}
    </>
  );
}
